import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Drawer, Table, Input, DatePicker, Button } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useLazyQuery } from '@apollo/client';
import {
    GET_PARTIAL_PICKUP_APPOINTMENTS,
    GetPartialPickupAppointmentsResponse,
    PartialPickupAppointmentItem,
} from '../graphql/queries/getPartialPickupAppointments';
import TablePaginationBar from '../../../../components/TablePaginationBar';
import SchedulePickupModal from './SchedulePickupModal';
import {
    GET_POP_APPOINTMENT_DETAIL,
    GetPopAppointmentDetailResponse,
} from '../graphql/queries/getPopAppointmentDetail';
import { useWarehouseContext } from '../../../../contexts/WarehouseContext';
import { normalizeTimezone } from '../../../../utils/timezoneUtils';
import { useTranslation } from 'react-i18next';

dayjs.extend(utc);
dayjs.extend(timezone);

interface Props {
    visible: boolean;
    onClose: () => void;
    /** 集散中心 Hub ID */
    hubId: number | null;
    /** 揽收日期，默认取进度页当前日期（YYYY-MM-DD） */
    defaultDate: string;
    /** 按行司机预筛选（主表格标红列点击带入） */
    initialDriver?: string;
}

interface AppliedFilters {
    date: string;
    orderNos: string;
    driver: string;
    phone: string;
    siteIds: string;
}

/** 部分揽收原因 → i18n key（值与后端 pickup_appointment.partial_pickup_reason 一一对应） */
const PARTIAL_REASON_I18N: Record<number, string> = {
    1: 'reasonNotReady',
    2: 'reasonVehicleFull',
    3: 'reasonOther',
};

/** 包裹单位 → 既有枚举 i18n key，不另起一套 */
const PARCEL_UNIT_I18N: Record<number, string> = {
    1: 'piece',
    2: 'bag',
    3: 'carton',
    4: 'pallet',
};

const splitMulti = (raw: string): string[] =>
    raw.split(/[,，\s]+/).map((s) => s.trim()).filter(Boolean);

/**
 * 揽收进度页「部分揽收成功预约单」抽屉。
 *
 * 不复用 PickupFailedAppointmentDrawer 的 mode 机制：那个组件的四个 mode 共用同一个 DTO、
 * 列基本一致；本抽屉的数据结构完全不同（多出数量、原因、复揽单三组字段）且操作列有三态，
 * 塞进去会把那四个已上线 mode 的代码淹没在条件分支里。
 */
const PartialPickupAppointmentDrawer: React.FC<Props> = ({ visible, onClose, hubId, defaultDate, initialDriver }) => {
    const { t } = useTranslation();
    const tq = (k: string) => t(`pickup.progress.partialPickup.${k}`);
    const tf = (k: string) => t(`pickup.progress.qualityFeedback.${k}`);

    const [date, setDate] = useState<Dayjs>(dayjs(defaultDate));
    const [orderNos, setOrderNos] = useState<string>('');
    const [driver, setDriver] = useState<string>('');
    const [phone, setPhone] = useState<string>('');
    const [siteIds, setSiteIds] = useState<string>('');
    const [pageNum, setPageNum] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(20);
    // 已提交的筛选快照：翻页用它，避免带上「未点查询」的实时输入
    const [applied, setApplied] = useState<AppliedFilters>(
        () => ({ date: defaultDate, orderNos: '', driver: initialDriver || '', phone: '', siteIds: '' }),
    );
    const [scheduleTarget, setScheduleTarget] = useState<{
        pickupSiteId?: number;
        address?: string;
        channelId?: number;
        appointmentId?: number;
        partialPickupReason?: number;
    } | null>(null);
    // 点按钮那一行的数据：部分揽收原因只有列表行有（详情接口不含），且详情查不到时要靠它降级开窗
    const pendingRowRef = useRef<{
        pickupSiteId?: number;
        address?: string;
        appointmentId?: number;
        partialPickupReason?: number;
    } | null>(null);

    /**
     * 操作列「安排司机上门」：列表查询 getPartialPickupAppointments **不返回 channelId**，
     * 直接开弹窗会让建单抽屉的「客户」为空、运营得自己再搜一遍。
     * 与揽收失败抽屉同一做法——点按钮时按 appointmentId 拉详情补齐渠道再开弹窗，
     * 保证与预约单详情页的「安排司机上门」带入口径一致。
     *
     * 查不到/查失败一律**按行数据降级开窗**（客户留空，退回到本次修复前的行为）。
     * 该详情走 Dubbo 到 pickup-service，不开窗会让运营连复揽都发起不了，比客户为空更糟。
     */
    const openScheduleFromRow = () => {
        if (pendingRowRef.current) {
            setScheduleTarget(pendingRowRef.current);
        }
    };

    const [loadScheduleTarget, { loading: scheduleTargetLoading }] = useLazyQuery<GetPopAppointmentDetailResponse>(
        GET_POP_APPOINTMENT_DETAIL,
        {
            fetchPolicy: 'network-only',
            onCompleted: (d) => {
                const detail = d?.getPopAppointmentDetail;
                if (!detail) {
                    openScheduleFromRow();
                    return;
                }
                setScheduleTarget({
                    pickupSiteId: detail.pickupSiteId,
                    address: detail.siteAddress,
                    channelId: detail.channelId,
                    appointmentId: detail.appointmentId,
                    partialPickupReason: pendingRowRef.current?.partialPickupReason,
                });
            },
            onError: (error) => {
                console.error('getPopAppointmentDetail failed, fallback to row data:', error);
                openScheduleFromRow();
            },
        },
    );

    const siteIdInvalid = splitMulti(siteIds).some((s) => Number.isNaN(Number(s)));

    const { currentWarehouse } = useWarehouseContext();
    const hubTz = normalizeTimezone(currentWarehouse?.timezone || 'UTC-7');
    // 上门时间是 UTC 墙钟的 LocalDateTime，与主表格及其它抽屉同口径按 hub 时区展示
    const formatArrivedAt = (value?: string) => {
        if (!value) return '--';
        const d = dayjs.utc(value).tz(hubTz);
        return d.isValid() ? d.format('YYYY/MM/DD HH:mm') : '--';
    };

    const formatQuantity = (count?: number, unit?: number) => {
        if (count == null) return '--';
        const unitKey = unit != null ? PARCEL_UNIT_I18N[unit] : undefined;
        const unitText = unitKey ? t(`enum.packageUnitFilterOptions.${unitKey}`) : '';
        return `${count}${unitText}`;
    };

    const [load, { data, loading }] = useLazyQuery<GetPartialPickupAppointmentsResponse>(
        GET_PARTIAL_PICKUP_APPOINTMENTS,
        { fetchPolicy: 'network-only' },
    );

    const runQuery = useCallback(
        (page: number, size: number, f: AppliedFilters) => {
            if (!hubId || !f.date) return;
            load({
                variables: {
                    filter: {
                        hubId,
                        pickupDate: f.date,
                        appointmentNumbers: splitMulti(f.orderNos),
                        driverKeyword: f.driver.trim() || null,
                        contactKeyword: f.phone.trim() || null,
                        pickupSiteIds: splitMulti(f.siteIds).map(Number).filter((n) => !Number.isNaN(n)),
                        pageNum: page,
                        pageSize: size,
                    },
                },
            });
        },
        [hubId, load],
    );

    useEffect(() => {
        if (visible) {
            const init: AppliedFilters = { date: defaultDate, orderNos: '', driver: initialDriver || '', phone: '', siteIds: '' };
            setDate(dayjs(defaultDate));
            setOrderNos('');
            setDriver(initialDriver || '');
            setPhone('');
            setSiteIds('');
            setApplied(init);
            setPageNum(1);
            // 清理上次遗留的弹窗状态，避免重开抽屉时安排弹窗自动弹出
            setScheduleTarget(null);
            runQuery(1, pageSize, init);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, defaultDate, hubId, initialDriver]);

    const onSearch = () => {
        if (siteIdInvalid) return;
        const next: AppliedFilters = {
            date: date.format('YYYY-MM-DD'),
            orderNos,
            driver,
            phone,
            siteIds,
        };
        setApplied(next);
        setPageNum(1);
        runQuery(1, pageSize, next);
    };

    const onPageChange = (page: number, size: number) => {
        setPageNum(page);
        setPageSize(size);
        runQuery(page, size, applied);
    };

    const page = data?.getPartialPickupAppointments;
    const rows = page?.content || [];
    const total = page?.totalElements || 0;

    const columns = [
        { title: tq('appointmentNumber'), dataIndex: 'appointmentNumber', width: 170, ellipsis: false },
        { title: tq('taskNumber'), dataIndex: 'taskNumber', width: 170, ellipsis: false },
        { title: tq('address'), dataIndex: 'address', width: 240 },
        {
            title: tq('reason'),
            dataIndex: 'partialPickupReason',
            width: 150,
            render: (v?: number) => (v != null && PARTIAL_REASON_I18N[v] ? tq(PARTIAL_REASON_I18N[v]) : '--'),
        },
        { title: tq('driver'), dataIndex: 'driverName', width: 120, render: (v?: string) => v || '--' },
        { title: tq('contact'), dataIndex: 'driverPhone', width: 150, render: (v?: string) => v || '--' },
        {
            title: tq('arrivedAt'),
            dataIndex: 'arrivedAt',
            width: 160,
            render: (v?: string) => formatArrivedAt(v),
        },
        {
            title: tq('expectedParcel'),
            dataIndex: 'expectedParcelCount',
            width: 120,
            render: (_: any, r: PartialPickupAppointmentItem) => formatQuantity(r.expectedParcelCount, r.expectedParcelUnit),
        },
        {
            title: tq('actualParcel'),
            dataIndex: 'actualParcelCount',
            width: 120,
            render: (_: any, r: PartialPickupAppointmentItem) => formatQuantity(r.actualParcelCount, r.actualParcelUnit),
        },
        {
            title: tq('repickupNumber'),
            dataIndex: 'repickupAppointmentNumber',
            width: 170,
            ellipsis: false,
            render: (v?: string) => v || '--',
        },
        {
            title: tq('action'),
            key: 'action',
            width: 140,
            fixed: 'right' as const,
            render: (_: any, r: PartialPickupAppointmentItem) => {
                // 已安排过就置灰并改文案：按钮的唯一作用是发起建单，重复发起会建出多张复揽单
                if (r.repickupScheduleType != null) {
                    return (
                        <Button type="link" disabled style={{ paddingLeft: 0 }}>
                            {r.repickupScheduleType === 1 ? tq('scheduledToday') : tq('scheduledNextDay')}
                        </Button>
                    );
                }
                return (
                    <Button
                        type="link"
                        style={{ paddingLeft: 0 }}
                        loading={scheduleTargetLoading}
                        onClick={() => {
                            pendingRowRef.current = {
                                pickupSiteId: r.pickupSiteId,
                                address: r.address,
                                appointmentId: r.appointmentId,
                                partialPickupReason: r.partialPickupReason,
                            };
                            loadScheduleTarget({ variables: { appointmentId: r.appointmentId } });
                        }}
                    >
                        {tf('schedule')}
                    </Button>
                );
            },
        },
    ];

    return (
        <Drawer title={tq('title')} open={visible} onClose={onClose} width={1200} destroyOnClose>
            <div className="grid grid-cols-5 gap-3 mb-4 items-end">
                <div>
                    <div className="text-xs text-gray-500 mb-1">{tf('filterDate')}</div>
                    <DatePicker value={date} onChange={(d) => d && setDate(d)} format="YYYY/MM/DD" allowClear={false} className="w-full" />
                </div>
                <div>
                    <div className="text-xs text-gray-500 mb-1">{tf('filterOrderNos')}</div>
                    <Input placeholder={tf('multiHint')} value={orderNos} onChange={(e) => setOrderNos(e.target.value)} />
                </div>
                <div>
                    <div className="text-xs text-gray-500 mb-1">{tf('filterDriver')}</div>
                    <Input placeholder={tf('fuzzyHint')} value={driver} onChange={(e) => setDriver(e.target.value)} />
                </div>
                <div>
                    <div className="text-xs text-gray-500 mb-1">{tf('filterContact')}</div>
                    <Input placeholder={tf('fuzzyHint')} value={phone} onChange={(e) => setPhone(e.target.value)} />
                </div>
                <div className="flex items-end gap-2">
                    <div className="flex-1">
                        <div className="text-xs text-gray-500 mb-1">{tf('filterSiteId')}</div>
                        <Input
                            placeholder={tf('multiHint')}
                            value={siteIds}
                            status={siteIdInvalid ? 'error' : undefined}
                            onChange={(e) => setSiteIds(e.target.value)}
                            onPressEnter={onSearch}
                        />
                        {siteIdInvalid && (
                            <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4 }}>{tf('siteIdNumericHint')}</div>
                        )}
                    </div>
                    <Button type="primary" onClick={onSearch} disabled={siteIdInvalid}>{tf('search')}</Button>
                </div>
            </div>

            <Table<PartialPickupAppointmentItem>
                loading={loading}
                rowKey="appointmentId"
                size="middle"
                scroll={{ x: 'max-content' }}
                dataSource={rows}
                pagination={false}
                columns={columns as any}
            />
            <TablePaginationBar
                total={total}
                current={pageNum}
                pageSize={pageSize}
                onChange={onPageChange}
                showQuickJumper
                pageSizeOptions={['20', '50', '100']}
            />

            <SchedulePickupModal
                visible={!!scheduleTarget}
                onClose={() => setScheduleTarget(null)}
                target={scheduleTarget}
                baseDate={applied.date}
            />
        </Drawer>
    );
};

export default PartialPickupAppointmentDrawer;
